import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import {
  COMPARISON_MODES,
  DATE_FORMAT,
} from 'lumen/utils/constants/net-revenue-constants';
import { WEEKS_IN_YEAR } from 'lumen/utils/constants/ri-constant-values';
import { getSdlyEnd } from 'lumen/utils/revenue-insight/controller-utils';

export interface DateShortcut {
  count?: number;
  id: string;
  isSelected?: boolean;
  range: { end: Dayjs; start: Dayjs };
  text: ReturnType<typeof tKey>;
}

export interface DateShortcutList {
  id: string;
  options: DateShortcut[];
}

export interface ComparisonPickerOption {
  displayRange: boolean;
  dropdownText?: ReturnType<typeof tKey>;
  hasCalendar: boolean;
  id: string;
  isDisabled: boolean;
  isSelected: boolean;
  range: { end: Dayjs; start: Dayjs };
  text: ReturnType<typeof tKey>;
}

export interface ComparisonPickerList {
  id: string;
  options: ComparisonPickerOption[];
  title?: string;
}

export function buildDatePickerRangeOptions(): DateShortcutList[] {
  const today = dayjs();

  const options: DateShortcut[] = [
    {
      text: tKey('net_revenue.current_month'),
      id: 'current_month',
      isSelected: true,
      range: {
        start: today.startOf('month'),
        end: today.endOf('month'),
      },
    },
    {
      text: tKey('net_revenue.current_month_plus'),
      count: 2,
      id: 'current_month_plus_2',
      isSelected: false,
      range: {
        start: today.startOf('month'),
        end: today.add(2, 'month').endOf('month'),
      },
    },
    {
      text: tKey('net_revenue.current_quarter'),
      id: 'quarter',
      isSelected: false,
      range: {
        start: today.startOf('quarter'),
        end: today.endOf('quarter'),
      },
    },
    {
      text: tKey('general.next_days'),
      count: 90,
      id: 'ninety_days',
      isSelected: false,
      range: {
        start: today,
        end: today.add(90, 'day'),
      },
    },
    {
      text: tKey('net_revenue.year_to_date'),
      id: 'year_to_date',
      isSelected: false,
      range: {
        start: today.startOf('year'),
        end: today,
      },
    },
    {
      text: tKey('net_revenue.full_year'),
      id: 'full_year',
      isSelected: false,
      range: {
        start: today.startOf('year'),
        end: today.endOf('year'),
      },
    },
    {
      text: tKey('general.custom'),
      id: 'custom',
      isSelected: false,
      range: {
        start: today.startOf('month'),
        end: today.endOf('month'),
      },
    },
  ];

  return [
    {
      id: 'references',
      options,
    },
  ];
}

export function getComparisonFromDateMoment(
  comparisonFromDate: string | null,
): Dayjs {
  return comparisonFromDate
    ? dayjs(comparisonFromDate)
    : dayjs().startOf('month').subtract(WEEKS_IN_YEAR, 'week');
}

/**
 * Derive the start date of the comparison window from the chosen
 * `comparisonMode` and the current period. Mirrors what `transition.checkRIDates`
 * does for revenue-insight: each preset mode has a fixed offset from `fromDate`,
 * and `custom` falls back to SWLY when nothing was previously selected.
 */
export function defaultComparisonFromDate(args: {
  comparisonMode: string;
  fromDate: string;
  toDate: string;
}): string {
  const from = dayjs(args.fromDate);
  const lengthInDays = dayjs(args.toDate).diff(from, 'day');

  switch (args.comparisonMode) {
    case COMPARISON_MODES.SDLY:
      return from.subtract(1, 'year').format(DATE_FORMAT);
    case COMPARISON_MODES.PP:
      return calculatePriorPeriodStart(from, lengthInDays).format(DATE_FORMAT);
    case COMPARISON_MODES.SWLY:
    case COMPARISON_MODES.CUSTOM:
    default:
      return from.subtract(WEEKS_IN_YEAR, 'week').format(DATE_FORMAT);
  }
}

export function calculateComparisonLengthInDays(range: {
  end: Dayjs;
  start: Dayjs;
}): number {
  return range.end.diff(range.start, 'day');
}

export function calculatePriorPeriodStart(
  rangeStart: Dayjs,
  comparisonLengthInDays: number,
): Dayjs {
  const startingDay = rangeStart.isoWeekday();
  const previousPeriodStart = rangeStart.subtract(
    comparisonLengthInDays,
    'day',
  );
  if (startingDay < previousPeriodStart.isoWeekday()) {
    return previousPeriodStart.isoWeekday(startingDay);
  }
  return previousPeriodStart.subtract(7, 'day').isoWeekday(startingDay);
}

export function calculateComparisonToDateMoment(args: {
  comparisonFromDateMoment: Dayjs;
  comparisonLengthInDays: number;
  comparisonMode: string;
  rangeStart: Dayjs;
}): Dayjs | null {
  const {
    rangeStart,
    comparisonMode,
    comparisonLengthInDays,
    comparisonFromDateMoment,
  } = args;

  if (comparisonLengthInDays >= 0) {
    if (comparisonMode === COMPARISON_MODES.SDLY) {
      return getSdlyEnd(
        rangeStart,
        comparisonFromDateMoment,
        comparisonLengthInDays,
      );
    }
    return comparisonFromDateMoment.add(comparisonLengthInDays, 'day');
  }

  return null;
}

export function buildComparisonDatePickerRangeOptions(args: {
  comparisonFromDateMoment: Dayjs;
  comparisonLengthInDays: number;
  comparisonMode: string;
  range: { end: Dayjs; start: Dayjs };
  vsComparisonTitle: string;
}): ComparisonPickerList[] {
  const {
    range,
    comparisonMode,
    comparisonLengthInDays,
    comparisonFromDateMoment,
    vsComparisonTitle,
  } = args;
  const { start } = range;
  const swly = start.subtract(WEEKS_IN_YEAR, 'week');
  const sdly = start.subtract(1, 'year');
  const ppStart = calculatePriorPeriodStart(start, comparisonLengthInDays);
  const ppEnd = ppStart.add(comparisonLengthInDays, 'day');
  const sdlyEnd = getSdlyEnd(start, sdly, comparisonLengthInDays);

  return [
    {
      id: 'comparisons',
      title: vsComparisonTitle,
      options: [
        {
          dropdownText: tKey('net_revenue.swly_short'),
          text: tKey('net_revenue.swly_long'),
          id: COMPARISON_MODES.SWLY,
          isSelected: comparisonMode === COMPARISON_MODES.SWLY,
          hasCalendar: true,
          displayRange: false,
          isDisabled: false,
          range: {
            start: swly,
            end: swly.add(comparisonLengthInDays, 'day'),
          },
        },
        {
          dropdownText: tKey('net_revenue.sdly_short'),
          text: tKey('net_revenue.sdly_long'),
          id: COMPARISON_MODES.SDLY,
          isSelected: comparisonMode === COMPARISON_MODES.SDLY,
          hasCalendar: true,
          displayRange: false,
          isDisabled: false,
          range: {
            start: sdly,
            end: sdlyEnd,
          },
        },
        {
          dropdownText: tKey('net_revenue.previous_period'),
          text: tKey('net_revenue.previous_period'),
          id: COMPARISON_MODES.PP,
          isSelected: comparisonMode === COMPARISON_MODES.PP,
          hasCalendar: true,
          displayRange: true,
          isDisabled: false,
          range: {
            start: ppStart,
            end: ppEnd,
          },
        },
        {
          text: tKey('general.custom'),
          id: COMPARISON_MODES.CUSTOM,
          isSelected: comparisonMode === COMPARISON_MODES.CUSTOM,
          hasCalendar: true,
          displayRange: true,
          isDisabled: false,
          range: {
            start: comparisonFromDateMoment,
            end: comparisonFromDateMoment.add(comparisonLengthInDays, 'day'),
          },
        },
      ],
    },
  ];
}
